import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { SearchPageParams, CollectionPageParams, CriteoRecommendationsResponse, CriteoRequestContext } from './types';

export class CriteoClient extends ApiClient {
  private readonly _criteoBaseUrl: string;
  private readonly _criteoPartnerId: string;
  private readonly _criteoToken?: string;

  constructor({
    _criteoBaseUrl,
    _criteoPartnerId,
    _criteoToken,
  }: {
    _criteoBaseUrl: string;
    _criteoPartnerId: string;
    _criteoToken?: string;
  }) {
    super();
    const criteoBaseUrl = _criteoBaseUrl.trim();
    const criteoPartnerId = _criteoPartnerId.trim();

    if (!criteoBaseUrl || !criteoPartnerId) {
      throw new Error('CriteoClient requires both _criteoBaseUrl and _criteoPartnerId');
    }

    this._criteoBaseUrl = criteoBaseUrl;
    this._criteoPartnerId = criteoPartnerId;
    this._criteoToken = _criteoToken;
  }

  protected get requestHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
    };
  }

  private _fetchSponsoredProducts = async (
    extraParams: Record<string, string>,
    {
      eventType,
      pageId,
      retailerVisitorId,
      customerId,
      email,
    }: CriteoRequestContext & { eventType: string; pageId: string }
  ): Promise<ApiResponse<CriteoRecommendationsResponse>> => {
    const queryParams = new URLSearchParams(
      Object.entries({
        'criteo-partner-id': this._criteoPartnerId,
        'retailervisitor-id': retailerVisitorId,
        'customer-id': customerId,
        email,
        'page-id': pageId,
        'event-type': eventType,
        ...extraParams,
      }).filter((entry): entry is [string, string] => entry[0] === 'item' || !!entry[1])
    );

    return await this._fetch<CriteoRecommendationsResponse>({
      path: `${this._criteoBaseUrl}?${queryParams.toString()}`,
      method: 'GET',
      headers: this._criteoToken ? { Authorization: `Bearer ${this._criteoToken}` } : undefined,
    });
  };

  getSearchSponsoredProducts = async (
    params: Omit<SearchPageParams, 'eventType'>
  ): Promise<ApiResponse<CriteoRecommendationsResponse>> => {
    return await this._fetchSponsoredProducts(
      { keywords: params.keywords },
      { ...params, eventType: 'viewSearchResult' }
    );
  };

  getCollectionSponsoredProducts = async (
    params: Omit<CollectionPageParams, 'eventType'>
  ): Promise<ApiResponse<CriteoRecommendationsResponse>> => {
    return await this._fetchSponsoredProducts(
      { category: params.collectionKey },
      { ...params, eventType: 'viewCategory' }
    );
  };
}
